// Full replacement PricingPage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Check,
  Crown,
  Zap,
  Shield,
  Users,
  Loader2,
  CheckCircle,
  XCircle,
  ArrowRight,
  Sparkles,
  BookOpen,
  Download,
  Gift,
  Clock,
  Target,
  Coins,
  Brain,
  Eye,
  Layers,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { PageLayout } from '../components/Layout';
import { SEO, PageSEO } from '../components/SEO';
import { apiClient, useAuth } from '../lib/api';
import { toast } from 'sonner';

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0 },
};

const calculateTimeLeft = (target) => {
  if (!target) return { expired: true };

  const difference = new Date(target) - new Date();

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / 1000 / 60) % 60),
    seconds: Math.floor((difference / 1000) % 60),
    expired: false,
  };
};

const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(targetDate));

  useEffect(() => {
    setTimeLeft(calculateTimeLeft(targetDate));

    if (!targetDate) return undefined;

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
};

const TokenPacksSection = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [packs, setPacks] = useState([]);

  useEffect(() => {
    apiClient
      .getTokenPacks()
      .then((res) => setPacks(res.data.packs || []))
      .catch(() => setPacks([]));
  }, []);

  const handleBuyTokens = async (packId) => {
    if (!user) {
      navigate('/register', { state: { from: '/pricing' } });
      return;
    }

    setLoading(packId);

    try {
      const response = await apiClient.createTokenPackCheckout(packId);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to create checkout session');
      console.error(error);
    } finally {
      setLoading(null);
    }
  };

  const getSavingsLabel = (packId) => {
    if (packId === 'pack_10') return 'Save 25%';
    if (packId === 'pack_20') return 'Save 38%';
    return null;
  };

  if (!packs.length) return null;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="grid sm:grid-cols-3 gap-4">
        {packs.map((pack) => {
          const savings = getSavingsLabel(pack.id);
          const featured = pack.id === 'pack_20';

          return (
            <Card
              key={pack.id}
              className={`relative border bg-white shadow-sm ${
                featured ? 'border-2 border-[#2CB67D] shadow-md' : 'border-slate-200'
              }`}
            >
              {savings && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-[#2CB67D] text-white text-xs shadow-sm">
                    {savings}
                  </Badge>
                </div>
              )}

              <CardContent className="pt-7 pb-5 text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-[#FF9F1C]/10 rounded-2xl flex items-center justify-center">
                  <Coins className="w-6 h-6 text-[#FF9F1C]" />
                </div>

                <h3 className="font-heading text-xl font-bold text-[#0B1D51] mb-1">
                  {pack.tokens} Tokens
                </h3>

                <p className="text-3xl font-bold text-[#0B1D51] mb-1">
                  £{Number(pack.price).toFixed(2)}
                </p>

                <p className="text-xs text-[#64748B] mb-4">
                  £{Number(pack.price_per_token).toFixed(2)} per attempt
                </p>

                <Button
                  className="w-full bg-[#FF9F1C] hover:bg-[#FF9F1C]/90 text-white"
                  onClick={() => handleBuyTokens(pack.id)}
                  disabled={loading === pack.id}
                  data-testid={`buy-${pack.id}-btn`}
                >
                  {loading === pack.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Buy tokens'
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <p className="text-center text-sm text-[#64748B] mt-4">
        Tokens never expire • Use on any test • Instant access after purchase
      </p>
    </div>
  );
};

const PriceCard = ({
  icon: Icon,
  title,
  subtitle,
  price,
  period,
  description,
  features,
  cta,
  onClick,
  href,
  loading,
  testId,
  featured = false,
  badge,
  footer,
  disabled = false,
}) => {
  const button = (
    <Button
      className={`w-full h-12 font-semibold ${
        featured
          ? 'bg-[#FF9F1C] hover:bg-[#FF9F1C]/90 text-white shadow-lg shadow-orange-500/20'
          : 'bg-white border border-[#0B1D51] text-[#0B1D51] hover:bg-[#0B1D51] hover:text-white'
      }`}
      variant={featured ? 'default' : 'outline'}
      onClick={onClick}
      disabled={disabled || loading}
      data-testid={testId}
    >
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Processing...
        </>
      ) : (
        <>
          {cta}
          <ArrowRight className="w-4 h-4 ml-2" />
        </>
      )}
    </Button>
  );

  return (
    <Card
      className={`relative h-full flex flex-col overflow-hidden ${
        featured
          ? 'border-2 border-[#FF9F1C] shadow-xl shadow-orange-500/10 bg-gradient-to-br from-white via-white to-orange-50/50'
          : 'border border-slate-200 bg-white shadow-sm'
      }`}
    >
      {badge && (
        <div
          className={`absolute top-0 right-0 px-4 py-1 text-xs font-bold text-white ${
            featured ? 'bg-[#FF9F1C]' : 'bg-[#0B1D51]'
          }`}
        >
          {badge}
        </div>
      )}

      <CardHeader className="pb-4">
        <div className="flex items-center gap-3 mb-3">
          <div
            className={`w-11 h-11 rounded-2xl flex items-center justify-center ${
              featured ? 'bg-[#FF9F1C]/10' : 'bg-[#0B1D51]/5'
            }`}
          >
            <Icon className={`w-5 h-5 ${featured ? 'text-[#FF9F1C]' : 'text-[#0B1D51]'}`} />
          </div>

          <div>
            <CardTitle className="font-heading text-2xl text-[#0B1D51]">
              {title}
            </CardTitle>
            <CardDescription>{subtitle}</CardDescription>
          </div>
        </div>

        <div>
          <span className="font-heading text-4xl font-bold text-[#0B1D51]">
            {price}
          </span>
          <span className="text-[#64748B]">{period}</span>
        </div>

        {description && (
          <p className="text-sm text-[#64748B] leading-relaxed">
            {description}
          </p>
        )}
      </CardHeader>

      <CardContent className="flex flex-col flex-1">
        <ul className="space-y-3 mb-6">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-3">
              <Check
                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  featured ? 'text-[#FF9F1C]' : 'text-[#2CB67D]'
                }`}
              />
              <span className="text-sm text-[#0F172A] leading-relaxed">
                {feature}
              </span>
            </li>
          ))}
        </ul>

        {footer && (
          <div className="mb-5 rounded-2xl bg-slate-50 border border-slate-200 p-3 text-sm text-[#475569]">
            {footer}
          </div>
        )}

        <div className="mt-auto">
          {href ? <Link to={href}>{button}</Link> : button}
        </div>
      </CardContent>
    </Card>
  );
};

export const PricingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(null);
  const [hasGuide, setHasGuide] = useState(false);
  const [saleData, setSaleData] = useState(null);
  const [tests, setTests] = useState([]);
  const [showTestSelectDialog, setShowTestSelectDialog] = useState(false);
  const [selectedTest, setSelectedTest] = useState('');

  useEffect(() => {
    apiClient
      .getSaleStatus()
      .then((res) => setSaleData(res.data))
      .catch(() => setSaleData(null));

    apiClient
      .getTests()
      .then((res) => setTests(res.data || []))
      .catch(() => setTests([]));
  }, []);

  useEffect(() => {
    if (!user) return;

    apiClient
      .checkGuideOwnership()
      .then((res) => setHasGuide(Boolean(res.data.has_guide)))
      .catch(() => setHasGuide(false));
  }, [user]);

  const timeLeft = useCountdown(saleData?.sale_end_utc);
  const isSaleActive = Boolean(saleData?.sale_active && !timeLeft.expired);

  const handleUpgrade = async (planType, testSlug = null) => {
    if (!user) {
      navigate('/register', { state: { from: '/pricing' } });
      return;
    }

    if (planType === 'single' && !testSlug) {
      setShowTestSelectDialog(true);
      return;
    }

    setLoading(planType);

    try {
      const originUrl = window.location.origin;
      const mappedPlanType =
        planType === 'monthly'
          ? 'premium'
          : planType === 'annual'
            ? 'complete'
            : planType === 'single'
              ? 'single_test'
              : planType;

      const response = await apiClient.createCheckout(originUrl, mappedPlanType, testSlug);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to create checkout session');
      console.error(error);
    } finally {
      setLoading(null);
    }
  };

  const handleSingleTestCheckout = async () => {
    if (!selectedTest) {
      toast.error('Please select a test');
      return;
    }

    setShowTestSelectDialog(false);
    await handleUpgrade('single', selectedTest);
  };

  const handleDownloadGuide = async () => {
    try {
      const response = await apiClient.downloadGuide();
      window.open(response.data.download_url, '_blank');
    } catch (error) {
      toast.error('Failed to download guide');
      console.error(error);
    }
  };

  const freeFeatures = [
    'Access to all assessment areas',
    '5 free test attempts',
    'View test information and skills',
    'See what each test assesses',
    'Basic score tracking',
    'Upgrade when ready',
  ];

  const singleFeatures = [
    'Choose 1 test area',
    'Unlimited attempts on that test',
    'Track your performance',
    'Target your weakest area',
    'Cancel anytime',
    'Upgrade anytime',
  ];

  const premiumFeatures = [
    'All levels unlocked',
    'Unlimited attempts',
    'Detailed performance analytics',
    'Progress tracking dashboard',
    'Score history and trends',
    'Priority support',
  ];

  const completeFeatures = [
    'All premium features included',
    'Unlimited access to all test areas',
    'Detailed performance analytics',
    'Progress tracking dashboard',
    'Score history and trends',
    'FREE Psychometric Guide included',
  ];

  const skillAreas = [
    { icon: Eye, label: 'Attention', text: 'Selective and divided attention practice' },
    { icon: Shield, label: 'Vigilance', text: 'Sustained concentration over time' },
    { icon: Brain, label: 'Memory & reasoning', text: 'Rules, procedures and decision-making' },
    { icon: Layers, label: 'Perception & reaction', text: 'Visual judgement and response accuracy' },
    { icon: Target, label: 'Coordination', text: 'Controlled response to complex stimuli' },
  ];

  return (
    <PageLayout>
      <SEO {...PageSEO.pricing} />

      <Dialog open={showTestSelectDialog} onOpenChange={setShowTestSelectDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-[#0B1D51]" />
              Select Your Test
            </DialogTitle>
            <DialogDescription>
              Choose which psychometric test you want full access to. You can change your selection in future billing periods.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Select value={selectedTest} onValueChange={setSelectedTest}>
              <SelectTrigger className="w-full" data-testid="test-select">
                <SelectValue placeholder="Select a test..." />
              </SelectTrigger>
              <SelectContent>
                {tests.map((test) => (
                  <SelectItem key={test.slug} value={test.slug}>
                    {test.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedTest && (
              <p className="text-sm text-slate-500 mt-3">
                You will get unlimited access to the{' '}
                <strong>{tests.find((test) => test.slug === selectedTest)?.name}</strong> test.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestSelectDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSingleTestCheckout}
              disabled={!selectedTest || loading === 'single'}
              className="bg-[#0B1D51] hover:bg-[#0B1D51]/90"
            >
              {loading === 'single' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Continue to checkout'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <section className="relative overflow-hidden bg-[#071633]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,159,28,0.22),_transparent_35%),radial-gradient(circle_at_bottom_right,_rgba(44,182,125,0.16),_transparent_30%)]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <Badge className="mb-5 bg-[#FF9F1C] text-white hover:bg-[#FF9F1C]/90">
                OPC · TOC · Private Assessment Preparation
              </Badge>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-5">
                Choose the right level of train driver psychometric practice.
              </h1>

              <p className="text-lg text-white/75 max-w-2xl leading-relaxed mb-8">
                Practise the skills assessed in train driver selection, including attention, vigilance,
                memory, reasoning, perception, reaction time and coordination.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  className="bg-[#FF9F1C] hover:bg-[#FF9F1C]/90 text-white h-12 px-6"
                  onClick={() => handleUpgrade('annual')}
                  disabled={loading === 'annual'}
                >
                  {loading === 'annual' ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Get best value package
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>

                <Link to={user ? '/dashboard' : '/register'}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-12 px-6 bg-white/10 border-white/20 text-white hover:bg-white hover:text-[#0B1D51]"
                  >
                    Start free
                  </Button>
                </Link>
              </div>

              <p className="text-xs text-white/50 mt-5">
                Preparation does not guarantee recruitment success. It helps you practise relevant skills before assessment.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <Card className="bg-white/95 backdrop-blur border-white/20 shadow-2xl">
                <CardContent className="p-6 sm:p-7">
                  <div className="flex items-center justify-between gap-4 mb-5">
                    <div>
                      <p className="text-sm font-semibold text-[#FF9F1C]">Recommended route</p>
                      <h2 className="font-heading text-2xl font-bold text-[#0B1D51]">
                        Complete Package
                      </h2>
                    </div>
                    <Badge className="bg-[#FF9F1C]/10 text-[#FF9F1C] hover:bg-[#FF9F1C]/10">
                      Best value
                    </Badge>
                  </div>

                  <div className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-5 mb-5">
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-4xl font-bold text-[#0B1D51]">£24.99</span>
                      <span className="text-[#64748B]">/year</span>
                    </div>
                    <p className="text-sm text-[#64748B]">
                      All test areas, unlimited attempts, analytics and the guide included.
                    </p>
                  </div>

                  <div className="space-y-3 mb-6">
                    {[
                      'Over £70 saving compared with monthly access',
                      'FREE Psychometric Guide worth £19.99',
                      'Most suitable if you are preparing seriously across multiple test areas',
                    ].map((item, index) => (
                      <div key={index} className="flex gap-3">
                        <CheckCircle className="w-5 h-5 text-[#2CB67D] flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-[#0F172A]">{item}</p>
                      </div>
                    ))}
                  </div>

                  <Button
                    className="w-full h-12 bg-[#FF9F1C] hover:bg-[#FF9F1C]/90 text-white font-semibold"
                    onClick={() => handleUpgrade('annual')}
                    disabled={loading === 'annual' || user?.is_member}
                  >
                    {user?.is_member ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Active Member
                      </>
                    ) : loading === 'annual' ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Get Complete Package
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: Shield, title: 'Secure Stripe payment', text: 'Card payments are processed securely.' },
              { icon: Brain, title: 'Rail-focused practice', text: 'Built around skills relevant to train driver selection.' },
              { icon: Users, title: 'Used by candidates', text: 'Designed for applicants preparing for competitive UK recruitment.' },
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3 rounded-2xl bg-[#F8FAFC] border border-slate-200 p-4">
                <div className="w-10 h-10 rounded-xl bg-[#0B1D51]/5 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-[#0B1D51]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0B1D51]">{item.title}</h3>
                  <p className="text-sm text-[#64748B]">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Badge className="bg-[#0B1D51]/10 text-[#0B1D51] hover:bg-[#0B1D51]/10 mb-4">
              Simple pricing
            </Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B1D51] mb-3">
              Pick the route that matches your preparation stage.
            </h2>
            <p className="text-[#64748B] max-w-3xl mx-auto">
              Start free, target one weak area, unlock everything monthly, or get the best value annual package with the guide included.
            </p>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 items-stretch">
            <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ delay: 0.05 }}>
              <PriceCard
                icon={Zap}
                title="Free"
                subtitle="Try before committing"
                price="£0"
                period="/limited access"
                description="A low-risk way to explore the platform and understand the assessment areas."
                features={freeFeatures}
                cta="Start free"
                href={user ? '/dashboard' : '/register'}
                testId="free-plan-btn"
              />
            </motion.div>

            <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ delay: 0.1 }}>
              <PriceCard
                icon={Target}
                title="Single Test"
                subtitle="Target one area"
                price="£4.99"
                period="/month"
                description="Best if you already know the exact test area you need to work on."
                features={singleFeatures}
                cta="Choose one test"
                onClick={() => handleUpgrade('single')}
                loading={loading === 'single'}
                testId="single-plan-btn"
              />
            </motion.div>

            <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ delay: 0.15 }}>
              <PriceCard
                icon={Crown}
                title="Premium"
                subtitle="Monthly full access"
                price="£7.99"
                period="/month"
                description="Unlock every test area and practise flexibly with monthly access."
                features={premiumFeatures}
                cta={user?.is_member ? 'Active Member' : 'Unlock full access'}
                onClick={() => handleUpgrade('monthly')}
                loading={loading === 'monthly'}
                disabled={user?.is_member}
                testId="monthly-plan-btn"
              />
            </motion.div>

            <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ delay: 0.2 }}>
              <PriceCard
                icon={Sparkles}
                title="Complete Package"
                subtitle="Best value annual access"
                price="£24.99"
                period="/year"
                description="The strongest option for serious preparation across the full assessment pathway."
                features={completeFeatures}
                cta={user?.is_member ? 'Active Member' : 'Get annual + free guide'}
                onClick={() => handleUpgrade('annual')}
                loading={loading === 'annual'}
                disabled={user?.is_member}
                testId="annual-plan-btn"
                featured
                badge={isSaleActive ? 'SALE' : 'BEST VALUE'}
                footer={
                  <div>
                    <p className="font-semibold text-[#0B1D51]">Includes FREE guide worth £19.99</p>
                    <p className="text-xs mt-1">Cancel anytime • Over £70 saving</p>
                  </div>
                }
              />
            </motion.div>
          </div>

          {isSaleActive && (
            <div className="mt-8 max-w-3xl mx-auto rounded-3xl border border-emerald-200 bg-emerald-50 p-5 text-center">
              <p className="text-sm text-emerald-700 mb-3 flex items-center justify-center gap-2">
                <Clock className="w-4 h-4" />
                Sale ends in
              </p>

              <div className="flex justify-center gap-2">
                {[
                  ['days', timeLeft.days],
                  ['hrs', timeLeft.hours],
                  ['min', timeLeft.minutes],
                  ['sec', timeLeft.seconds],
                ].map(([label, value]) => (
                  <div key={label} className="bg-white rounded-xl px-3 py-2 border border-emerald-200 min-w-[64px]">
                    <span className="text-xl font-bold text-emerald-600">
                      {String(value).padStart(2, '0')}
                    </span>
                    <span className="text-[10px] text-emerald-700 block uppercase tracking-wide">
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="py-14 sm:py-16 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 items-start">
            <div>
              <Badge className="bg-[#0B1D51]/10 text-[#0B1D51] hover:bg-[#0B1D51]/10 mb-4">
                What you are practising
              </Badge>

              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B1D51] mb-4">
                Built around the skills assessed in train driver selection.
              </h2>

              <p className="text-[#64748B] leading-relaxed mb-5">
                RIS-3751-TOM identifies selection criteria including attention, vigilance, memory,
                reasoning, perception, reaction time, hand coordination, communication and behavioural
                aptitudes. TDF practice is designed to help candidates build familiarity with the type of
                mental effort these areas require.
              </p>

              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  TDF does not provide official OPC tests and cannot guarantee assessment outcomes.
                  The purpose is preparation, familiarity, repetition and skill development.
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {skillAreas.map((item, index) => (
                <div key={index} className="rounded-3xl border border-slate-200 bg-[#F8FAFC] p-5">
                  <div className="w-11 h-11 rounded-2xl bg-[#0B1D51]/5 flex items-center justify-center mb-4">
                    <item.icon className="w-5 h-5 text-[#0B1D51]" />
                  </div>
                  <h3 className="font-heading font-bold text-[#0B1D51] mb-1">
                    {item.label}
                  </h3>
                  <p className="text-sm text-[#64748B] leading-relaxed">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_0.9fr] gap-8 items-center">
            <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/70 to-white shadow-sm">
              <CardContent className="p-6 sm:p-8">
                <Badge className="bg-emerald-500/10 text-emerald-700 border-emerald-200 mb-4">
                  <BookOpen className="w-3 h-3 mr-1" />
                  Included with Complete Package
                </Badge>

                <h2 className="font-heading text-3xl font-bold text-[#0B1D51] mb-3">
                  TDF Psychometric Assessment Guide
                </h2>

                <p className="text-[#64748B] leading-relaxed mb-6">
                  A downloadable preparation guide covering assessment areas, practical preparation
                  advice and examples to support structured revision alongside interactive practice.
                </p>

                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                  {[
                    'All assessment areas covered',
                    'RIS-3751-TOM informed preparation',
                    'Practical exercises and examples',
                    'Tips from qualified assessors',
                    'Downloadable PDF format',
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-[#0B1D51]">{feature}</span>
                    </div>
                  ))}
                </div>

                {hasGuide ? (
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={handleDownloadGuide}
                    data-testid="download-guide-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Your Guide
                  </Button>
                ) : (
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => handleUpgrade('annual')}
                    disabled={loading === 'annual'}
                    data-testid="guide-annual-btn"
                  >
                    {loading === 'annual' ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Gift className="w-4 h-4 mr-2" />
                        Get Complete Package + FREE Guide
                      </>
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>

            <div className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-8">
              <p className="text-sm font-semibold text-emerald-700 mb-2">
                Usually £19.99
              </p>
              <p className="font-heading text-4xl font-bold text-[#0B1D51] mb-3">
                FREE with Complete Package
              </p>
              <p className="text-[#64748B] leading-relaxed mb-5">
                The guide works best alongside unlimited practice. Read the strategy, practise the
                skill, review your performance, then repeat.
              </p>

              <div className="space-y-3">
                {[
                  'Understand the test area',
                  'Practise under realistic pressure',
                  'Track your scores and trends',
                  'Focus on weaker areas before assessment',
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold flex items-center justify-center">
                      {index + 1}
                    </div>
                    <span className="text-sm text-[#0F172A]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Badge className="bg-[#FF9F1C]/10 text-[#FF9F1C] border-[#FF9F1C]/20 mb-4">
              <Zap className="w-3 h-3 mr-1" />
              Pay as you go
            </Badge>

            <h2 className="text-3xl font-heading font-bold text-[#0B1D51]">
              Token Packs
            </h2>

            <p className="text-[#64748B] mt-2 max-w-2xl mx-auto">
              Need more attempts without a subscription? Buy tokens for flexible practice.
            </p>
          </div>

          <TokenPacksSection user={user} />
        </div>
      </section>

      <section className="py-14 sm:py-16 bg-[#0B1D51]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
            Serious about your assessment preparation?
          </h2>

          <p className="text-white/70 max-w-2xl mx-auto mb-7">
            The Complete Package gives you the clearest preparation route: all tests, unlimited
            attempts, analytics and the guide included.
          </p>

          <Button
            size="lg"
            className="bg-[#FF9F1C] hover:bg-[#FF9F1C]/90 text-white h-12 px-7"
            onClick={() => handleUpgrade('annual')}
            disabled={loading === 'annual' || user?.is_member}
          >
            {user?.is_member ? (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Active Member
              </>
            ) : loading === 'annual' ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Get Complete Package
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </section>
    </PageLayout>
  );
};

export const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState('checking');
  const sessionId = searchParams.get('session_id');

  const checkPaymentStatus = useCallback(async () => {
    if (!sessionId) {
      navigate('/pricing');
      return;
    }

    for (let attempt = 0; attempt < 6; attempt += 1) {
      try {
        const response = await apiClient.getPaymentStatus(sessionId);

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          await refreshUser();
          return;
        }
      } catch (error) {
        console.error('Payment status check failed:', error);

        if (attempt === 5) {
          setStatus('error');
          return;
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 2000));
    }

    setStatus('pending');
  }, [sessionId, navigate, refreshUser]);

  useEffect(() => {
    checkPaymentStatus();
  }, [checkPaymentStatus]);

  return (
    <PageLayout showFooter={false}>
      <div className="min-h-[80vh] flex items-center justify-center bg-[#F8FAFC] px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full"
        >
          <Card className="border-slate-200 text-center shadow-sm">
            <CardContent className="pt-8 pb-6">
              {status === 'checking' && (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 bg-[#0B1D51]/5 rounded-full flex items-center justify-center">
                    <Loader2 className="w-10 h-10 text-[#0B1D51] animate-spin" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
                    Processing Payment
                  </h2>
                  <p className="text-[#64748B]">
                    Please wait while we confirm your payment.
                  </p>
                </>
              )}

              {status === 'success' && (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 bg-[#2CB67D]/10 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-[#2CB67D]" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
                    Payment Successful
                  </h2>
                  <p className="text-[#64748B] mb-6">
                    Your access is now active. You can start practising from your dashboard.
                  </p>
                  <Link to="/dashboard">
                    <Button className="bg-[#0B1D51] hover:bg-[#0B1D51]/90" data-testid="go-dashboard-btn">
                      Go to Dashboard
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </>
              )}

              {status === 'pending' && (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 bg-[#FF9F1C]/10 rounded-full flex items-center justify-center">
                    <Loader2 className="w-10 h-10 text-[#FF9F1C]" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
                    Payment Processing
                  </h2>
                  <p className="text-[#64748B] mb-6">
                    Your payment is still processing. Please check your email for confirmation.
                  </p>
                  <Link to="/dashboard">
                    <Button variant="outline">
                      Go to Dashboard
                    </Button>
                  </Link>
                </>
              )}

              {status === 'error' && (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                    <XCircle className="w-10 h-10 text-red-500" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
                    Something Went Wrong
                  </h2>
                  <p className="text-[#64748B] mb-6">
                    We could not verify your payment. Please contact support if you were charged.
                  </p>
                  <Link to="/pricing">
                    <Button variant="outline">
                      Try Again
                    </Button>
                  </Link>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </PageLayout>
  );
};

export const GuideSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('checking');
  const [downloadUrl, setDownloadUrl] = useState(null);
  const sessionId = searchParams.get('session_id');

  const checkGuideStatus = useCallback(async () => {
    if (!sessionId) {
      navigate('/pricing');
      return;
    }

    for (let attempt = 0; attempt < 6; attempt += 1) {
      try {
        const response = await apiClient.getGuideStatus(sessionId);

        if (response.data.payment_status === 'paid') {
          setStatus('success');

          const downloadResponse = await apiClient.downloadGuide();
          setDownloadUrl(downloadResponse.data.download_url);
          return;
        }
      } catch (error) {
        console.error('Guide status check failed:', error);

        if (attempt === 5) {
          setStatus('error');
          return;
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 2000));
    }

    setStatus('pending');
  }, [sessionId, navigate]);

  useEffect(() => {
    checkGuideStatus();
  }, [checkGuideStatus]);

  const handleDownload = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  };

  return (
    <PageLayout showFooter={false}>
      <div className="min-h-[80vh] flex items-center justify-center bg-[#F8FAFC] px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full"
        >
          <Card className="border-slate-200 text-center shadow-sm">
            <CardContent className="pt-8 pb-6">
              {status === 'checking' && (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 bg-emerald-50 rounded-full flex items-center justify-center">
                    <Loader2 className="w-10 h-10 text-emerald-600 animate-spin" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
                    Processing Purchase
                  </h2>
                  <p className="text-[#64748B]">
                    Please wait while we confirm your purchase.
                  </p>
                </>
              )}

              {status === 'success' && (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 bg-emerald-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-emerald-600" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
                    Purchase Successful
                  </h2>
                  <p className="text-[#64748B] mb-6">
                    Your TDF Psychometric Guide is ready to download.
                  </p>

                  <div className="space-y-3">
                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleDownload}
                      disabled={!downloadUrl}
                      data-testid="download-guide-success-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Guide PDF
                    </Button>

                    <Link to="/tests" className="block">
                      <Button variant="outline" className="w-full">
                        <ArrowRight className="w-4 h-4 mr-2" />
                        Browse Practice Tests
                      </Button>
                    </Link>
                  </div>
                </>
              )}

              {status === 'pending' && (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 bg-[#FF9F1C]/10 rounded-full flex items-center justify-center">
                    <Loader2 className="w-10 h-10 text-[#FF9F1C]" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
                    Purchase Processing
                  </h2>
                  <p className="text-[#64748B] mb-6">
                    Your purchase is still processing. Please check your email for confirmation and download details.
                  </p>
                  <Link to="/pricing">
                    <Button variant="outline">
                      Return to Pricing
                    </Button>
                  </Link>
                </>
              )}

              {status === 'error' && (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                    <XCircle className="w-10 h-10 text-red-500" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
                    Something Went Wrong
                  </h2>
                  <p className="text-[#64748B] mb-6">
                    We could not verify your purchase. Please contact support if you were charged.
                  </p>
                  <Link to="/pricing">
                    <Button variant="outline">
                      Try Again
                    </Button>
                  </Link>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </PageLayout>
  );
};